/* eslint-disable react/no-unknown-property */
import React from 'react';
import { Icon, gridConfig } from '@tvg/fuse-design';
import { analyticsEvent } from '@sd-utility/analytics';
import { GetImageSources, GetMainImages, GetImageUrl } from 'utilities/media/media-util';
import { useSelectedSkuContext } from 'context/SelectedSku/SelectedSkuContext';
import { ImageZoomerInstanceInterface } from 'types/ImageZoomer';
import findFirstSkuByColourOption from 'utilities/sku/findFirstSkuByColourOption';
import IconChevronRight from '@tvg/fuse-icons/lib/ChevronRight';
import IconChevronLeft from '@tvg/fuse-icons/lib/ChevronLeft';
import { Splide } from '@splidejs/react-splide';
import ImageZoomer from './assets/image-zoomer';
import { CarouselSlideProps, ImageCarouselProps, CarouselOptions, VideoSource } from '../../types/ImageCarouselTypes';
import {
  OuterContainer,
  InnerContainer,
  MainCarousel,
  ThumbnailCarousel,
  Slide,
  ArrowsContainer,
  NextArrow,
  PreviousArrow,
  PlayOverlay,
  StyledVideoPlayer,
} from './ImageCarouselStyles';

const { useEffect, useState, useRef, useCallback } = React;

const imageSizes = {
  mainSlider: [...GetMainImages().mainSlider],
  thumbnails: [
    {
      media: `(max-width: ${gridConfig.m.maxWidth})`,
      template: '$pdp_48x64_x2$',
    },
    {
      media: `(max-width: ${gridConfig.m.maxWidth})`,
      template: '$70x93_standard$',
    },
    {
      media: `(min-width: ${gridConfig.m.minWidth})`,
      template: '$pdp_70x93_x2$',
    },
  ],
};

const carouselTransitionSpeed = 500;
const carouselOptions: CarouselOptions = {
  main: {
    pagination: false,
    lazyLoad: 'sequential',
    speed: carouselTransitionSpeed,
  },
  thumbnails: {
    arrows: false,
    direction: 'ltr',
    isNavigation: true,
    mediaQuery: 'min',
    pagination: false,
    type: 'slide',
    breakpoints: {
      // Can't use rem values for breakpoints in splide :(
      1024: {
        direction: 'ttb',
        fixedWidth: 'auto',
        fixedHeight: 'auto',
        height: '100%',
      },
    },
  },
};

const analyticsEventCategory = 'Product Image Carousel';
const VIDEO_THUMBNAIL_CLASS = 'video-thumbnail';

const handleCarouselArrowClickEvent = (arrowType: string) => {
  analyticsEvent({
    category: analyticsEventCategory,
    action: 'Arrow Click',
    label: arrowType,
    data: {
      name: `${analyticsEventCategory} - Arrow Click`,
      id: arrowType,
    },
    send: {
      googleAnalytics: true,
      speedtrap: true,
    },
  });
};

const renderCarouselArrows = () => (
  <ArrowsContainer className="splide__arrows">
    <PreviousArrow
      className="splide__arrow splide__arrow--prev"
      data-testid="product_imageCarousel_previousArrow"
      onClick={() => handleCarouselArrowClickEvent('Previous')}
    >
      <Icon icon={<IconChevronLeft />} width="32px" height="32px" />
    </PreviousArrow>

    <NextArrow
      className="splide__arrow splide__arrow--next"
      data-testid="product_imageCarousel_nextArrow"
      onClick={() => handleCarouselArrowClickEvent('Next')}
    >
      <Icon icon={<IconChevronRight />} width="32px" height="32px" />
    </NextArrow>
  </ArrowsContainer>
);

const CarouselSlide = React.memo(
  ({
    firstImage,
    totalImageLength,
    image,
    index,
    productTitle,
    areThumbnails,
    roundel,
    videos,
    site,
  }: CarouselSlideProps) => {
    const imageIdentifier = index === 0 ? firstImage?.identifier : image.identifier;
    const altText = `Image ${index + 1} of ${totalImageLength} of ${productTitle}`;
    const fetchPriority = index === 0 && !areThumbnails ? 'high' : 'low';
    const imageSources = GetImageSources(areThumbnails ? imageSizes.thumbnails : imageSizes.mainSlider);
    const imageProps = areThumbnails
      ? {
          src: GetImageUrl({
            identifier: imageIdentifier,
            template:
              videos && videos?.[0].url === imageIdentifier
                ? imageSizes.thumbnails[1].template
                : imageSizes.thumbnails[0].template,
            format: 'jpg',
            isVideo: !!(videos && videos?.[0].url === imageIdentifier),
            site,
          }),
          alt: altText.replace('Image', 'Image thumbnail'),
          width: 70,
          height: 93,
          fetchPriority,
          loading: 'lazy',
          className: videos && videos?.[0].url === imageIdentifier ? VIDEO_THUMBNAIL_CLASS : undefined,
        }
      : {
          src: GetImageUrl({
            identifier: imageIdentifier,
            template: imageSizes.mainSlider[0].template,
            format: 'jpg',
            site,
          }),
          alt: altText,
          width: 300,
          height: 400,
          loading: index > 0 ? 'lazy' : null,
          fetchPriority,
          className: 'product-image-carousel__image',
          'data-image-number': index + 1,
        };

    const pictureSources = imageSources.map(({ media, template, format }) => {
      const url = GetImageUrl({
        identifier: imageIdentifier,
        isVideo: !!(videos && videos?.[0].url === imageIdentifier),
        template,
        format,
        roundel: areThumbnails ? undefined : roundel,
        site,
      });

      return <source key={url} type={`image/${format}`} srcSet={url} media={media} />;
    });

    return (
      <Slide key={image.identifier}>
        <picture>
          {pictureSources}
          {imageProps.className === VIDEO_THUMBNAIL_CLASS && (
            <PlayOverlay id="video-overlay" data-testid="video-overlay" />
          )}
          <img
            style={{ width: '100%' }}
            src={imageProps.src}
            alt={imageProps.alt}
            width={imageProps.width}
            height={imageProps.height}
            fetchpriority={imageProps?.fetchPriority as 'high' | 'low' | undefined}
            loading={imageProps?.loading as 'lazy' | 'eager' | undefined}
            className={imageProps.className}
            data-image-number={imageProps['data-image-number']}
          />
        </picture>
      </Slide>
    );
  },
);

function ImageCarousel({ images, productTitle, roundel, videos, site }: ImageCarouselProps) {
  const mainImages = images.filter((image) => image.identifier !== videos?.[0]?.url);
  const thumbnailImages = videos ? [...images, { identifier: videos?.[0].url }] : images;
  const mainCarouselRef = useRef<Splide>(null);
  const thumbnailCarouselRef = useRef<Splide>(null);
  const { selectedOptions, skus } = useSelectedSkuContext();
  const [firstImage, setFirstImage] = useState(mainImages[0]);
  const imageZoomerInstancesArray: any[] = [];
  const [previousSelectedColour, setPreviousSelectedColour] = useState('');
  const [imageZoomerInstances, setImageZoomerInstances] = useState(imageZoomerInstancesArray) as [
    ImageZoomerInstanceInterface[],
    (instances: ImageZoomerInstanceInterface[]) => void,
  ];
  const [imageIsZoomed, setImageIsZoomed] = useState(false);

  const enableThumbnailCarousel = () => {
    if (mainCarouselRef.current && thumbnailCarouselRef.current && thumbnailCarouselRef.current.splide) {
      mainCarouselRef.current.sync(thumbnailCarouselRef.current.splide);
    }
  };

  const temporarilyDisableThumbnailCarousel = () => {
    if (thumbnailCarouselRef.current) {
      const { current } = thumbnailCarouselRef;
      const disabledClassName = 'is-disabled';

      if (current.splide) {
        const { splide } = current;
        splide.root.classList.add(disabledClassName);

        setTimeout(() => {
          splide.root.classList.remove(disabledClassName);
        }, carouselTransitionSpeed);
      }
    }
  };

  const handleImageZoomInEvent = useCallback(() => {
    const activeImageZoomerInstance = imageZoomerInstances.find((instance) => instance.active);

    setImageIsZoomed(true);

    if (activeImageZoomerInstance) {
      const { element } = activeImageZoomerInstance;

      analyticsEvent({
        category: analyticsEventCategory,
        action: 'Image Zoom',
        label: `Image ${element.dataset.imageNumber}`,
        data: {
          name: `${analyticsEventCategory} - Image Zoom`,
          id: `Image${element.dataset.imageNumber}`,
        },
        send: {
          googleAnalytics: true,
          speedtrap: true,
        },
      });
    }
  }, [imageZoomerInstances, setImageIsZoomed, analyticsEventCategory]);

  const handleImageZoomOutEvent = useCallback(() => {
    setImageIsZoomed(false);
  }, [setImageIsZoomed]);

  const enableImageZoomers = () => {
    const mainSliderImages = document.querySelectorAll('.product-image-carousel__image');

    mainSliderImages.forEach((image) => {
      const imageZoomerInstance = ImageZoomer.create(image, {
        zoomedInCallback: handleImageZoomInEvent,
        zoomedOutCallback: handleImageZoomOutEvent,
      });

      imageZoomerInstance.enable();
      imageZoomerInstancesArray.push(imageZoomerInstance);
    });

    setImageZoomerInstances(imageZoomerInstancesArray);
  };

  const zoomOutActiveImageZoomers = (instances = imageZoomerInstances) => {
    const activeImageZoomerInstances = instances.filter((instance) => instance.active);

    activeImageZoomerInstances.forEach((instance) => {
      instance.zoomOut();
    });
  };
  const handleSlideChange = () => {
    const videoPlayerContainer = document.getElementById('main-carousel-video-player');
    const videoPlayer = videoPlayerContainer?.querySelector('video');

    if (videoPlayer) {
      videoPlayer.pause();
    }
  };

  useEffect(() => {
    if (mainCarouselRef && mainCarouselRef.current) {
      mainCarouselRef?.current?.splide?.on('moved', handleSlideChange);
    }

    // Clean up the event listener when the component unmounts
    return () => {
      if (mainCarouselRef && mainCarouselRef.current) {
        mainCarouselRef?.current?.splide?.off('moved');
      }
    };
  }, []);

  const videoUrl = (orientation: string, url: string) => {
    let baseUrl;
    if (site === 'very') {
      baseUrl = 'https://media.very.co.uk/v/very';
    } else if (site === 'littlewoods') {
      baseUrl = 'https://media.littlewoods.com/v/littlewoods';
    } else if (site === 'veryie') {
      baseUrl = 'https://media.very.ie/v/littlewoodsireland';
    }

    if (orientation === 'PORTRAIT') {
      return {
        webm: `${baseUrl}/${url}/p_webm_400x533?protocol=https`,
        mp4: `${baseUrl}/${url}/p_mp4_400x533?protocol=https`,
      };
    }
    if (orientation === 'LANDSCAPE') {
      return {
        webm: `${baseUrl}/${url}/l_webm_640x360?protocol=https`,
        mp4: `${baseUrl}/${url}/l_mp4_640x360?protocol=https`,
      };
    }

    return undefined;
  };

  const getPosterUrl = (url: string): string => {
    if (site === 'very') {
      return `https://media.very.co.uk/v/very/${url}`;
    }
    if (site === 'littlewoods') {
      return `https://media.littlewoods.com/v/littlewoods/${url}`;
    }
    if (site === 'veryie') {
      return `https://media.very.ie/v/littlewoodsireland/${url}`;
    }
    return '';
  };

  const renderCarouselSlides = (areThumbnails = false) => {
    const totalImageLength = videos ? images.length + 1 : images.length;
    const slides = (areThumbnails ? thumbnailImages : mainImages).map((image, index) => (
      <CarouselSlide
        firstImage={firstImage}
        totalImageLength={totalImageLength}
        image={image}
        index={index}
        productTitle={productTitle}
        areThumbnails={areThumbnails}
        videos={videos}
        roundel={roundel}
        site={site}
      />
    ));

    if (videos && !areThumbnails) {
      const { url, orientation } = videos[0];
      const videoFormat = videoUrl(orientation, url);
      const videoPoster = getPosterUrl(url);
      const videoSources: VideoSource[] = [];
      const mp4 = videoFormat?.mp4;
      const webm = videoFormat?.webm;

      if (mp4) {
        videoSources.push({
          src: mp4,
          type: 'mp4',
        });
      }

      if (webm) {
        videoSources.push({
          src: webm,
          type: 'webm; codecs="vp8, vorbis"',
        });
      }

      if (videoPoster && videoSources.length > 0) {
        slides.push(
          <Slide key={url}>
            <StyledVideoPlayer
              id="main-carousel-video-player"
              poster={videoPoster}
              preload="none"
              sources={videoSources}
            />
          </Slide>,
        );
      }
    }

    return slides;
  };

  const handleCarouselMoveEvent = useCallback(
    (splide: object, destIndex: number) => {
      const imageNumber = destIndex + 1;

      zoomOutActiveImageZoomers(imageZoomerInstancesArray);
      temporarilyDisableThumbnailCarousel();
      analyticsEvent({
        category: analyticsEventCategory,
        action: 'Image Move',
        label: `Image ${imageNumber}`,
        data: {
          name: `${analyticsEventCategory} - Image Move`,
          id: `Image${imageNumber}`,
        },
        send: {
          googleAnalytics: true,
          speedtrap: true,
        },
      });
    },
    [zoomOutActiveImageZoomers, temporarilyDisableThumbnailCarousel, analyticsEventCategory],
  );

  const handleSelectedSkuChange = () => {
    if (selectedOptions.colour && previousSelectedColour !== selectedOptions.colour) {
      const firstSku = findFirstSkuByColourOption(skus, selectedOptions.colour);
      setPreviousSelectedColour(selectedOptions.colour);
      setFirstImage(firstSku?.images ? firstSku.images[0] : firstImage);
      zoomOutActiveImageZoomers();

      if (mainCarouselRef.current) mainCarouselRef.current.go(0);
    }
  };

  const imageZoomersInitialised = useRef(false);

  useEffect(() => {
    enableThumbnailCarousel();

    if (!imageZoomersInitialised.current) {
      enableImageZoomers();
      imageZoomersInitialised.current = true;
    }
  }, []);

  useEffect(handleSelectedSkuChange, [selectedOptions.colour]);

  return (
    <OuterContainer data-testid="carousel">
      <InnerContainer>
        <MainCarousel
          options={{ drag: !imageIsZoomed, ...carouselOptions.main }}
          renderControls={renderCarouselArrows}
          onMove={handleCarouselMoveEvent}
          ref={mainCarouselRef}
          className="persuasive-message"
        >
          {renderCarouselSlides()}
        </MainCarousel>
        <ThumbnailCarousel options={carouselOptions.thumbnails} ref={thumbnailCarouselRef}>
          {renderCarouselSlides(true)}
        </ThumbnailCarousel>
      </InnerContainer>
    </OuterContainer>
  );
}

export default ImageCarousel;
